import React, { useEffect, useRef } from 'react'
import { useStateValue } from '@State'
import { Zoom } from 'react-toastify'
import { toast, Toaster } from '@CommonComponents/toast'

/**
 * Renders a brief "Saved" toast popup when the question's answer finishes saving.
 * Each question gets its own isolated Toaster via containerId so toasts are
 * visually anchored to the question rather than pooled at the canvas level.
 *
 * The parent element must have `position: relative` for the absolute Toaster to
 * sit in the top-right corner of the question card.
 */
export const SaveIndicator = ({ questionGuid }) => {
  const [state] = useStateValue()
  const isSaving = state.savingGuids.has(questionGuid)
  const prevRef = useRef(false)
  const toastIdRef = useRef(null)

  useEffect(() => {
    if (prevRef.current && !isSaving) {
      // Dismiss the previous toast immediately so queued toasts don't
      // pile up and play in succession. The exit animation of the old
      // toast combined with the entrance of the new one gives the
      // retreat-and-rerender effect.
      if (toastIdRef.current !== null) {
        toast.dismiss(toastIdRef.current)
      }
      toastIdRef.current = toast.success('Saved', {
        containerId: questionGuid,
        autoClose: 1500,
        hideProgressBar: true,
        closeButton: false,
        style: { padding: '4px 10px', minHeight: 'unset' },
        bodyStyle: { padding: 0, margin: 0, fontSize: '0.85rem' },
      })
    }
    prevRef.current = isSaving
  }, [isSaving, questionGuid])

  return (
    <Toaster
      containerId={questionGuid}
      position="top-right"
      transition={Zoom}
      autoClose={1500}
      hideProgressBar
      closeButton={false}
      style={{
        position: 'absolute',
        '--toastify-toast-offset': '0px',
        '--toastify-toast-width': 'fit-content',
        '--toastify-toast-min-height': 'unset',
      }}
    />
  )
}
